import { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import Button from '../../../components/Button';
import { UserIdSearch } from '../../../components/UserIdSearch';
import MarkdownPreview from './MarkdownPreview';
import { adminGetLessonPreview } from '../services/learningService';
import type { LessonPreviewResponse } from '../services/learningTypes';
import type { ObsessLessUser } from '../../../types/commonTypes';
import { useAuth } from '../../../hooks/useAuth';

interface LessonPreviewModalProps {
    sectionId: string;
    lessonId: string;
    lessonTitle: string;
    onClose: () => void;
}

type ModalStep = 'search' | 'loading' | 'preview';

const LessonPreviewModal = ({ sectionId, lessonId, lessonTitle, onClose }: LessonPreviewModalProps) => {
    const [step, setStep] = useState<ModalStep>('search');
    const [error, setError] = useState<string | null>(null);
    const [duration, setDuration] = useState<number>(0);
    const [lessonData, setLessonData] = useState<LessonPreviewResponse | null>(null);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
    const [showOnlyCustomized, setShowOnlyCustomized] = useState(true);
    const { user } = useAuth();

    const handleUserSelected = async (user: ObsessLessUser | null) => {
        if (!user) return;

        setSelectedUserName(user.displayName || user.email || user.uid);
        setStep('loading');
        setError(null);

        try {
            const startTime = performance.now();
            const result = await adminGetLessonPreview(sectionId, lessonId, user.uid);
            const endTime = performance.now();
            const duration = endTime - startTime;
            setDuration(duration);
            setLessonData(result);
            setCurrentPageIndex(0);
            setStep('preview');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to fetch lesson preview';
            setError(message);
            setStep('search');
        }
    };

    const handleBackToSearch = () => {
        setStep('search');
        setLessonData(null);
        setCurrentPageIndex(0);
        setSelectedUserName(null);
        setShowOnlyCustomized(false);
    };

    const visiblePages = showOnlyCustomized
        ? (lessonData?.pages.filter((p) => p.hasCustomizedContent) ?? [])
        : (lessonData?.pages ?? []);

    const hasAnyCustomizedPages = (lessonData?.pages.some((p) => p.hasCustomizedContent)) ?? false;

    const handlePrevPage = () => {
        setCurrentPageIndex((prev) => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPageIndex((prev) => Math.min(visiblePages.length - 1, prev + 1));
    };

    const currentPage = visiblePages[currentPageIndex];
    const totalPages = visiblePages.length;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-full sm:min-h-[1000px] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                            Preview: {lessonTitle}
                        </h3>
                        {selectedUserName && step === 'preview' && (
                            <p className="text-sm text-gray-500 truncate">
                                As: {selectedUserName}
                            </p>
                        )}
                    </div>
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        size="sm"
                        icon={<X size={20} />}
                    />
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    {step === 'search' && (
                        <div className="p-4 sm:p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Search for a user to preview this lesson with their personalized content.
                            </p>
                            <UserIdSearch onSearchResult={handleUserSelected} />
                            {error && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                    {error}
                                </div>
                            )}
                            {user && (
                                <>
                                    <p className="text-sm text-gray-600 mb-4">Or use the current user: <span className="font-bold">{user.displayName}</span></p>
                                    <Button
                                        onClick={() => handleUserSelected({
                                            uid: user.uid,
                                            displayName: user.displayName || user.email || user.uid,
                                            email: user.email || '',
                                            createdAt: new Date(),
                                            hasFinishedOnboarding: false,
                                            onboardingProgress: '',
                                        })}
                                        variant="primary"
                                        size="sm"
                                    >
                                        Use Current User ({user.displayName})
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    {step === 'loading' && (
                        <div className="flex-1 flex flex-col items-center justify-center p-12 gap-4">
                            <Loader2 size={32} className="animate-spin text-blue-600" />
                            <p className="text-sm text-gray-600">
                                Generating personalized preview for {selectedUserName}...
                            </p>
                        </div>
                    )}

                    {step === 'preview' && currentPage && (
                        <>
                            {/* Phone-like preview container */}
                            <div className="flex-1 h-full overflow-hidden mx-4 sm:mx-6 mt-4 mb-2 border border-gray-300 rounded-2xl bg-white shadow-inner flex flex-col">
                                <div className="flex-1 h-full flex flex-col justify-center items-center overflow-y-auto">
                                    <MarkdownPreview
                                        markdown={currentPage.content}
                                        buttonText={currentPage.buttonText}
                                        isQuestionPage={currentPage.type === 'question'}
                                        options={currentPage.options ?? []}
                                        highlightCustomContent
                                        customizedFallbacks={currentPage.customizedFallbacks}
                                    />
                                </div>
                            </div>

                            {/* Page navigation */}
                            <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 border-t border-gray-200">
                                <Button
                                    onClick={handleBackToSearch}
                                    variant="outline"
                                    size="sm"
                                >
                                    Change User
                                </Button>
                                <label className={`flex items-center gap-1.5 text-xs select-none ${hasAnyCustomizedPages ? 'cursor-pointer text-gray-700' : 'cursor-not-allowed text-gray-400'}`}>
                                    <input
                                        type="checkbox"
                                        checked={showOnlyCustomized}
                                        onChange={(e) => {
                                            setShowOnlyCustomized(e.target.checked);
                                            setCurrentPageIndex(0);
                                        }}
                                        disabled={!hasAnyCustomizedPages}
                                        className="rounded"
                                    />
                                    Customized only
                                </label>
                                <div className="flex items-center gap-3">
                                    <Button
                                        onClick={handlePrevPage}
                                        variant="outline"
                                        size="sm"
                                        disabled={currentPageIndex === 0}
                                        icon={<ChevronLeft size={16} />}
                                    />
                                    <span className="text-sm text-gray-600 tabular-nums">
                                        {currentPageIndex + 1} / {totalPages}
                                    </span>
                                    <Button
                                        onClick={handleNextPage}
                                        variant="outline"
                                        size="sm"
                                        disabled={currentPageIndex >= totalPages - 1}
                                        icon={<ChevronRight size={16} />}
                                    />
                                </div>
                            </div>
                            <div className="px-4 sm:px-6 py-3 border-t border-gray-200">
                                Generated in {duration.toFixed(0)}ms
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LessonPreviewModal;
